/**
 * Utility functions for formatting percentages with internationalization support
 */

/**
 * Formats a decimal number as a percentage with specified decimal places
 * and locale-specific formatting
 *
 * @param value - The decimal value to format (e.g., 0.1234 for 12.34%)
 * @param locale - The locale to use for formatting (e.g., 'en-US', 'pl-PL', 'de-DE')
 * @param decimalPlaces - Number of decimal places to show (default: 1)
 * @returns Formatted percentage string
 *
 * @example
 * formatPercentage(0.1234, 'en-US', 2) // "12.34%"
 * formatPercentage(0.1234, 'pl-PL', 1) // "12,3%"
 * formatPercentage(0.1234, 'de-DE', 0) // "12 %"
 */
export function formatPercentage(
  value: number,
  locale: string,
  decimalPlaces: number = 1,
): string {
  if (!Number.isFinite(value)) {
    return '0%';
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value);
  } catch (error) {
    // Fallback to en-US if locale is not supported
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(value);
  }
}

/**
 * Formats a percentage value that's already in percentage form (e.g., 12.34 for 12.34%)
 * with locale-specific formatting
 *
 * @param percentageValue - The percentage value (e.g., 12.34 for 12.34%)
 * @param locale - The locale to use for formatting
 * @param decimalPlaces - Number of decimal places to show (default: 1)
 * @returns Formatted percentage string
 *
 * @example
 * formatPercentageValue(12.34, 'en-US', 2) // "12.34%"
 * formatPercentageValue(12.34, 'pl-PL', 1) // "12,3%"
 */
export function formatPercentageValue(
  percentageValue: number,
  locale: string,
  decimalPlaces: number = 1,
): string {
  return formatPercentage(percentageValue / 100, locale, decimalPlaces);
}

/**
 * Creates a percentage formatter function with preset locale and decimal places
 *
 * @param locale - The locale to use for formatting
 * @param decimalPlaces - Number of decimal places to show (default: 1)
 * @returns A function that formats decimal values as percentages
 *
 * @example
 * const formatPL = createPercentageFormatter('pl-PL', 2)
 * formatPL(0.1234) // "12,34%"
 */
export function createPercentageFormatter(
  locale: string,
  decimalPlaces: number = 1,
): (value: number) => string {
  return (value: number) => formatPercentage(value, locale, decimalPlaces);
}

/**
 * Gets the percentage symbol for a given locale
 *
 * @param locale - The locale to get the percentage symbol for
 * @returns The percentage symbol (usually "%" but may vary by locale)
 */
export function getPercentageSymbol(locale: string): string {
  try {
    const formatter = new Intl.NumberFormat(locale, { style: 'percent' });
    const parts = formatter.formatToParts(0.01);
    const percentPart = parts.find((part) => part.type === 'percentSign');
    return percentPart?.value || '%';
  } catch (error) {
    return '%';
  }
}

/**
 * Formats a ratio (0-1) as a percentage with locale support
 * Commonly used for occupation ratios, completion percentages, etc.
 *
 * @param ratio - The ratio value between 0 and 1
 * @param locale - The locale to use for formatting
 * @param decimalPlaces - Number of decimal places to show (default: 1)
 * @returns Formatted percentage string
 *
 * @example
 * formatRatioAsPercentage(0.756, 'en-US', 1) // "75.6%"
 * formatRatioAsPercentage(0.756, 'pl-PL', 0) // "76%"
 */
export function formatRatioAsPercentage(
  ratio: number,
  locale: string,
  decimalPlaces: number = 1,
): string {
  if (ratio < 0 || ratio > 1) {
    console.warn(`Ratio value ${ratio} is outside expected range [0, 1]`);
  }

  return formatPercentage(
    Math.max(0, Math.min(1, ratio)),
    locale,
    decimalPlaces,
  );
}
